import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface InAppNotificationDto {
  id: number;
  trigger_key: string;
  title: string;
  body?: string;
  priority?: string;
  action_url?: string | null;
  module?: string | null;
  object_type?: string | null;
  object_id?: number | null;
  notification_id?: number;
  is_read: boolean;
  read_at?: string | null;
  expires_at?: string | null;
  created_at: string;
}

export interface InAppNotificationsPage {
  items: InAppNotificationDto[];
  page: number;
  page_size: number;
  total: number;
  unread_count: number;
}

interface InAppNotificationsApiResponse {
  status: string;
  message: string | null;
  data: InAppNotificationsPage;
}

interface SimpleApiResponse {
  status: string;
  message: string | null;
}

function notificationsBaseUrl(): string {
  const env = environment as { notificationsApiUrl?: string };
  const override = env.notificationsApiUrl?.trim();
  if (override) {
    return override.replace(/\/?$/, '/');
  }
  const root = environment.apiUrl.replace(/\/?$/, '');
  return `${root}/api/v1/`;
}

function hasUserToken(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.localStorage !== 'undefined' &&
    !!localStorage.getItem('token')
  );
}

@Injectable({
  providedIn: 'root',
})
export class InAppNotificationService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = notificationsBaseUrl();

  getInAppNotifications(
    page = 1,
    pageSize = 20,
    isRead?: boolean,
    search?: string,
    module?: string,
  ): Observable<InAppNotificationsPage> {
    if (!hasUserToken()) {
      return EMPTY;
    }

    let params = new HttpParams().set('page', String(page)).set('page_size', String(pageSize));
    if (isRead === true) {
      params = params.set('is_read', 'true');
    } else if (isRead === false) {
      params = params.set('is_read', 'false');
    }
    const q = search?.trim();
    if (q) {
      params = params.set('search', q);
    }
    const moduleFilter = module?.trim();
    if (moduleFilter) {
      params = params.set('module', moduleFilter);
    }
    const url = `${this.baseUrl}notifications/in-app`;
    return this.http.get<InAppNotificationsApiResponse>(url, { params }).pipe(
      map((res) => {
        if (res.status !== 'success' || !res.data) {
          throw new Error(res.message ?? 'Failed to load notifications');
        }
        return res.data;
      }),
    );
  }

  markInAppNotificationsRead(ids: number[]): Observable<void> {
    if (!hasUserToken()) {
      return EMPTY;
    }

    const url = `${this.baseUrl}notifications/in-app/read`;
    return this.http.post<SimpleApiResponse>(url, { ids }).pipe(
      map((res) => {
        if (res.status !== 'success') {
          throw new Error(res.message ?? 'Failed to mark notifications as read');
        }
      }),
    );
  }
}
